import { locale, t } from "@/lib/i18n";

/* Сквозная аналитика, шаг 1: словарь «откуда пришёл клиент» (contacts.ads / deals.utm, см. backend/hub/ads.py) → простые фразы.
   Один и тот же текст показывается в карточке контакта и сделки. */

export type Ads = Partial<
  Record<
    | "source" | "medium" | "campaign" | "term" | "content" | "gclid" | "fbclid" | "adName" | "adId" | "adGroup" | "keyword" | "network"
    | "clickedAt" | "promo" | "campaignSource" | "clickType" | "socialLeadId" | "referrer" | "firstUrl" | "firstVisitAt" | "device" | "leadSource" | "from",
    string
  >
>;

export type SourceFirst = { channel?: string; channelId?: string; at?: string } | null | undefined;

const CHANNEL: Record<string, string> = {
  whatsapp: "WhatsApp",
  telegram: "Telegram",
  email: t("почта"),
  wazzup: t("WhatsApp (архив Wazzup)"),
  instagram: "Instagram",
  facebook: "Facebook",
  viber: "Viber",
  phone: t("звонок"),
};

const day = (iso?: string | null) => {
  if (!iso) return "";
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? iso : d.toLocaleDateString(locale);
};
const when = (iso?: string | null) => {
  if (!iso) return "";
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? iso : d.toLocaleString(locale, { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" });
};

/** название рекламной площадки по utm_source / gclid / fbclid */
export function adPlatform(a: Ads): string {
  const s = (a.source ?? "").toLowerCase();
  if (s.includes("google") || (!s && a.gclid)) return "Google Ads";
  if (["facebook", "fb", "meta", "instagram", "ig"].some((k) => s.includes(k)) || (!s && a.fbclid)) return "Meta (Facebook / Instagram)";
  if (s.includes("tiktok")) return "TikTok";
  if (s.includes("yandex")) return t("Яндекс Директ");
  if (s.includes("linkedin")) return "LinkedIn";
  if (s.includes("snap")) return "Snapchat";
  if (s.includes("bing") || s.includes("microsoft")) return "Microsoft Ads";
  return a.source ?? "";
}

/** фразы простым языком: откуда клиент, по какой рекламе, первое обращение */
export function describeAds(a: Ads | null | undefined, first?: SourceFirst): string[] {
  const out: string[] = [];
  if (first?.channel) out.push(t("Первое обращение: {channel}{when}", { channel: CHANNEL[first.channel] ?? first.channel, when: first.at ? `, ${when(first.at)}` : "" }));
  if (!a) return out;
  const platform = adPlatform(a);
  const hasAd = Boolean(a.source || a.gclid || a.fbclid || a.campaign || a.adName || a.keyword);
  if (hasAd) {
    const parts: string[] = [];
    if (a.campaign) parts.push(t("кампания «{v}»", { v: a.campaign }));
    if (a.adGroup) parts.push(t("группа «{v}»", { v: a.adGroup }));
    if (a.adName) parts.push(t("объявление «{v}»", { v: a.adName }));
    if (a.keyword || a.term) parts.push(t("по запросу «{v}»", { v: a.keyword ?? a.term ?? "" }));
    if (a.medium) parts.push(t("тип «{v}»", { v: a.medium }));
    if (a.content) parts.push(t("вариант «{v}»", { v: a.content }));
    if (a.network) parts.push(t("сеть {v}", { v: a.network }));
    if (a.clickedAt) parts.push(t("клик {date}", { date: day(a.clickedAt) }));
    const head = platform ? t("Пришёл с рекламы {platform}", { platform }) : t("Пришёл с рекламы");
    out.push(parts.length ? `${head}: ${parts.join(", ")}.` : `${head}.`);
    if (a.gclid || a.fbclid) out.push(t("Клик по рекламе зафиксирован ({kind}) — для сверки расходов с площадкой.", { kind: a.gclid ? "gclid" : "fbclid" }));
  }
  if (a.promo) out.push(t("Промокод: {v}.", { v: a.promo }));
  if (a.firstUrl) out.push(t("Первый визит на сайт: {url}{when}.", { url: a.firstUrl.replace(/^https?:\/\//, ""), when: a.firstVisitAt ? ` (${day(a.firstVisitAt)})` : "" }));
  if (a.referrer) out.push(t("Перешёл с: {v}.", { v: a.referrer.replace(/^https?:\/\//, "") }));
  if (a.leadSource && !hasAd) out.push(t("Источник в Zoho CRM: {v}.", { v: a.leadSource }));
  else if (a.leadSource) out.push(t("В Zoho CRM источник записан как «{v}».", { v: a.leadSource }));
  return out;
}

export const channelLabel = (kind: string) => CHANNEL[kind] ?? kind;
